/**
 * British Columbia Subject Comments queries + mutations.
 *
 * Per the BC K-12 Student Reporting Policy: descriptive feedback per
 * subject anchored to the Provincial Proficiency Scale (Em / Dev /
 * Prof / Ext). Curricular Competency chips per (subject, grade).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post, patch } from "../../client";
import { teacherKeys } from "../teacher";

// ─────────────────────────────────────────────────────────────────
// Types — mirror backend/src/models/bc-comment.model.ts
// ─────────────────────────────────────────────────────────────────

export type Pronoun = "He/Him" | "She/Her" | "They/Them";
export type BCProficiency = "EM" | "DV" | "PR" | "EX";
export type ChipState = "neutral" | "strength" | "weakness";

export interface BCChip {
  label: string;
  state: ChipState;
}

export interface BCSubjectComment {
  _id: string;
  name: string;
  pronoun: Pronoun;
  subject: string;
  subjectName?: string;
  gradeLevel: string;
  proficiency: BCProficiency;
  chips: BCChip[];
  comment: string;
  finalComment?: string;
  wasEdited: boolean;
  createdAt: string;
  classProfileId?: string;
  classProfileName?: string;
}

export interface GenerateBCSubjectCommentInput {
  name: string;
  pronoun: Pronoun;
  subject: string;
  subjectName?: string;
  gradeLevel: string;
  proficiency: BCProficiency;
  chips: BCChip[];
  sessionId?: string;
  classProfileId?: string;
}

// ─────────────────────────────────────────────────────────────────
// Query keys
// ─────────────────────────────────────────────────────────────────

export const subjectCommentsKeys = {
  all: ["bc-subject-comments"] as const,
  histories: () => [...subjectCommentsKeys.all, "history"] as const,
  history: () => [...subjectCommentsKeys.histories(), "list"] as const,
  detail: (id: string) => [...subjectCommentsKeys.all, "detail", id] as const,
};

// ─────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────

export function useSubjectCommentsHistory() {
  return useQuery({
    queryKey: subjectCommentsKeys.history(),
    queryFn: async () => {
      const res = await get<{ comments: BCSubjectComment[] }>(`/bc/history?limit=50`);
      return res.comments;
    },
  });
}

export function useSubjectCommentsDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? subjectCommentsKeys.detail(id) : ["bc-subject-comments", "detail", "disabled"],
    queryFn: async () => {
      if (!id) return null;
      const res = await get<{ comment: BCSubjectComment }>(`/bc/${id}`);
      return res.comment;
    },
    enabled: !!id,
  });
}

export function useGenerateSubjectComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: GenerateBCSubjectCommentInput) => {
      const res = await post<{ comment: BCSubjectComment }>("/bc/generate", input);
      return res.comment;
    },
    onSuccess: () => {
      // `comment_generate_succeeded` now fires from bc.service.ts.
      // See learningSkills.ts for the same rationale.
      queryClient.invalidateQueries({ queryKey: subjectCommentsKeys.histories() });
      queryClient.invalidateQueries({ queryKey: teacherKeys.all });
    },
  });
}

export function useUpdateSubjectComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      finalComment,
      wasEdited,
    }: {
      id: string;
      finalComment: string;
      wasEdited: boolean;
    }) => {
      await patch(`/bc/${id}`, { finalComment, wasEdited });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subjectCommentsKeys.histories() });
    },
  });
}
